import { FieldRow, Select } from '@platform/design-system';
import {
  Controller,
  useWatch,
  type FieldValues,
  type Path,
  type PathValue,
  type UseFormReturn,
} from 'react-hook-form';
import { usePageContext } from '../usePageContext.ts';
import { delegationChoicesOf, governorateChoicesOf, keepingCurrent, type AreaKey } from './area-choices.ts';
import type { Geography } from './useGeoAreas.ts';

export type AreaFieldsProps<Form extends FieldValues> = {
  form: UseFormReturn<Form>;
  geography: Geography | undefined;
  by: AreaKey;
  governorate: Path<Form>;
  delegation: Path<Form>;
  labels: { readonly governorate: string; readonly delegation: string };
  required?: boolean;
  governorateError?: string | undefined;
};

const NO_AREA = '';

const areaOf = (value: unknown): string => (typeof value === 'string' ? value : NO_AREA);

export const AreaFields = <Form extends FieldValues>({
  form,
  geography,
  by,
  governorate,
  delegation,
  labels,
  required = false,
  governorateError,
}: AreaFieldsProps<Form>) => {
  const { t } = usePageContext();
  const chosenGovernorate = areaOf(useWatch({ control: form.control, name: governorate }));
  return (
    <FieldRow>
      <Controller
        control={form.control}
        name={governorate}
        render={({ field }) => (
          <Select
            label={labels.governorate}
            required={required}
            placeholder={t('newOrder.selectGovernorate')}
            options={keepingCurrent(governorateChoicesOf(geography, by), areaOf(field.value))}
            errorText={governorateError}
            {...field}
            onChange={(event) => {
              field.onChange(event);
              form.setValue(delegation, NO_AREA as PathValue<Form, Path<Form>>, { shouldDirty: true });
            }}
          />
        )}
      />
      <Controller
        control={form.control}
        name={delegation}
        render={({ field }) => (
          <Select
            label={labels.delegation}
            options={[
              { value: NO_AREA, label: t('newOrder.anyDelegation') },
              ...keepingCurrent(delegationChoicesOf(geography, by, chosenGovernorate), areaOf(field.value)),
            ]}
            {...field}
          />
        )}
      />
    </FieldRow>
  );
};
